import React, { useState } from 'react';
import { CheckCircle2, AlertTriangle, Globe, Database, Loader2, ExternalLink, EyeOff, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  unpublishMarketingArticle, verifyMarketingArticleLive, getMarketingContentCheck, getMarketingArticle,
} from '../../services/api';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '');

export const WarningList = ({ warnings, testid }) => {
  if (!warnings?.length) return null;
  return (
    <ul className="space-y-1.5" data-testid={testid}>
      {warnings.map((w) => (
        <li key={w.code} className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
          <span className="font-semibold">{w.message}</span>
          {w.count > 1 && <span className="text-amber-700"> ×{w.count}</span>}
          {w.samples?.length > 0 && (
            <div className="font-mono text-amber-800 mt-0.5 break-all">{w.samples.join('  ·  ')}</div>
          )}
        </li>
      ))}
    </ul>
  );
};

const Row = ({ icon: Icon, ok, title, detail, testid, children }) => (
  <div className="flex items-start gap-3 py-2.5" data-testid={testid}>
    <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${ok ? 'text-emerald-600' : 'text-slate-400'}`} />
    <div className="flex-1 min-w-0">
      <div className={`text-sm font-medium ${ok ? 'text-emerald-800' : 'text-slate-800'}`}>{title}</div>
      {detail && <div className="text-xs text-slate-500 mt-0.5 break-words">{detail}</div>}
      {children}
    </div>
  </div>
);

export const ContentApiStatus = ({ article, onChanged }) => {
  const [busy, setBusy] = useState(null);
  const [check, setCheck] = useState(null);
  const inApi = article.status === 'published' && !!article.publicPublishedAt && !!article.publicContentHash;
  const version = article.publicVersion;
  const pendingChanges = inApi && article.updatedAt && article.publicPublishedAt
    && new Date(article.updatedAt) > new Date(article.publicPublishedAt);
  const verified = inApi && article.websiteVerifiedVersion === version;
  const v = article.websiteVerification;
  const apiUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/insights/${article.slug}`;

  const refresh = async () => onChanged(await getMarketingArticle(article.id));

  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) {
      toast.error(e?.response?.data?.detail || 'Request failed');
    } finally { setBusy(null); }
  };

  const verify = () => run('verify', async () => {
    const r = await verifyMarketingArticleLive(article.id);
    if (r.verified) toast.success(`Verified live on website (v${r.expectedVersion})`);
    else toast.warning(r.message);
    await refresh();
  });

  const unpublish = () => {
    if (!window.confirm('Unpublish this article? It will disappear from the content API (404) and the website. LinkedIn posts are not removed.')) return;
    run('unpublish', async () => {
      await unpublishMarketingArticle(article.id);
      toast.success('Unpublished — removed from the content API');
      await refresh();
    });
  };

  const runCheck = () => run('check', async () => setCheck(await getMarketingContentCheck(article.id)));

  return (
    <div className="divide-y divide-slate-100" data-testid="content-api-status">
      <Row
        icon={inApi ? CheckCircle2 : Database}
        ok={inApi}
        testid="content-api-state"
        title={inApi ? `Published to content API · v${version}` : 'Not in content API'}
        detail={inApi ? `Saved ${fmt(article.publicPublishedAt)}` : 'Publish saves a validated public copy the website reads.'}
      >
        {pendingChanges && (
          <div className="text-xs text-amber-700 mt-1" data-testid="content-api-pending">
            Unpublished edits since v{version} — publish again to update the API.
          </div>
        )}
        {inApi && (
          <a href={apiUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1 mt-1" data-testid="content-api-link">
            View public JSON <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </Row>
      <Row
        icon={verified ? CheckCircle2 : (v && !v.verified && inApi ? AlertTriangle : Globe)}
        ok={verified}
        testid="website-verify-state"
        title={verified ? `Verified live on website · v${version}` : 'Not verified live on website'}
        detail={v ? `${v.message || ''} Checked ${fmt(v.checkedAt)}` : 'Use Verify live once the website has refreshed.'}
      />
      {article.lastWebhook && (
        <div className="text-[11px] text-slate-500 py-2" data-testid="content-api-webhook">
          Website refresh webhook: {article.lastWebhook.ok ? `OK (${article.lastWebhook.status})` : `failed — ${article.lastWebhook.error || article.lastWebhook.status}`} · {fmt(article.lastWebhook.at)}
        </div>
      )}
      <div className="flex flex-wrap gap-2 pt-3">
        <Button size="sm" variant="outline" onClick={verify} disabled={!inApi || !!busy} data-testid="verify-live-btn">
          {busy === 'verify' ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Globe className="h-3.5 w-3.5 mr-1.5" />} Verify live
        </Button>
        <Button size="sm" variant="outline" onClick={runCheck} disabled={!!busy} data-testid="content-check-btn">
          {busy === 'check' ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ListChecks className="h-3.5 w-3.5 mr-1.5" />} Check formatting
        </Button>
        <Button size="sm" variant="outline" onClick={unpublish} disabled={!inApi || !!busy} className="text-red-700 border-red-200 hover:bg-red-50" data-testid="unpublish-btn">
          <EyeOff className="h-3.5 w-3.5 mr-1.5" /> Unpublish
        </Button>
      </div>
      {check && (
        <div className="pt-3 space-y-2" data-testid="content-check-result">
          {check.errors.length > 0 && (
            <ul className="text-[11px] text-red-800 bg-red-50 border border-red-200 rounded px-2 py-1.5 space-y-0.5" data-testid="content-check-errors">
              {check.errors.map((e) => <li key={e}>{e}</li>)}
            </ul>
          )}
          {check.errors.length === 0 && check.warnings.length === 0 && (
            <div className="text-xs text-emerald-700" data-testid="content-check-ok">Ready to publish — {check.blockCount} blocks, no formatting issues.</div>
          )}
          <WarningList warnings={check.warnings} testid="content-check-warnings" />
        </div>
      )}
      {!check && article.publicWarnings?.length > 0 && (
        <div className="pt-3">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">Warnings at last publish</div>
          <WarningList warnings={article.publicWarnings} testid="content-api-warnings" />
        </div>
      )}
    </div>
  );
};
